/**
 * Employment input (input_subtype: employment) — Alpine component.
 * Moved from the inline script in components/employment-input.blade.php: inline
 * blade scripts die when the component first appears via a Livewire morph
 * (morphdom doesn't execute injected script tags), which left the whole field
 * dead — no autocomplete AND no way to save manual input.
 *
 * Manual entry binds unconditionally in init(); Google Places is a progressive
 * enhancement layered on top when the central loader resolves. Every keystroke
 * in the address search persists the raw string into employment.address so a
 * typed address survives Continue even if the user never presses Enter or
 * selects a suggestion — with or without Google.
 */
import loadGoogleMaps from '../lib/google-maps-loader';
import alvvaParsePlace from '../lib/google-place-parser';
import { unitField } from './unit-field';

export default function employmentInput(modelValue) {
    return {
        autocomplete: null,
        showParsedAddress: false,
        employment: {
            employer: '',
            job_title: '',
            address: '',
            start_date: '',
            end_date: '',
            currently_employed: false
        },
        parsedAddress: {
            street: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            country: ''
        },
        modelValue: modelValue,

        // Apt/Ste/Flr asked as a choice plus a number, stored back into the
        // same key as one canonical string -- the payload shape is unchanged.
        ...unitField('parsedAddress.address2', 'updateAddressFromParsed'),

        init() {
            this.bindManualEntry();
            loadGoogleMaps()
                .then(() => this.initAutocomplete())
                .catch(() => {}); // silent fallback — manual entry already works

            this.parseExistingValue();
            this.watchUnit();

            this.$watch('modelValue', (value) => {
                if (value !== this.formatValue()) {
                    this.parseExistingValue();
                }
            });
        },

        /** Always-on manual path: never depends on Google. */
        bindManualEntry() {
            const input = this.$refs.addressSearchInput;
            if (!input || input.dataset.manualBound === '1') return;
            input.dataset.manualBound = '1';

            // Persist raw text on every keystroke so nothing is lost if the
            // user taps Continue without Enter/blur/selection.
            input.addEventListener('input', () => {
                if (!this.showParsedAddress) {
                    this.parsedAddress.street = input.value.trim();
                    this.updateAddressFromParsed();
                }
            });

            // Handle Enter key for manual entry
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = input.value.trim();
                    if (value) {
                        this.parsedAddress.street = value;
                        this.showParsedAddress = true;
                        this.updateAddressFromParsed();
                    }
                }
            });

            // Delay lets a click on a Places suggestion win: place_changed sets
            // showParsedAddress before this timeout inspects it.
            input.addEventListener('blur', () => {
                setTimeout(() => {
                    if (!this.showParsedAddress && input.value.trim()) {
                        this.parsedAddress.street = input.value.trim();
                        this.showParsedAddress = true;
                        this.updateAddressFromParsed();
                    }
                }, 250);
            });
        },

        initAutocomplete() {
            const input = this.$refs.addressSearchInput;
            if (!input || input.dataset.pacBound === '1') return;
            input.dataset.pacBound = '1';

            this.autocomplete = new google.maps.places.Autocomplete(input, {
                fields: ["address_components", "formatted_address"],
                types: ["address"]
            });

            this.autocomplete.addListener("place_changed", () => {
                const place = this.autocomplete.getPlace();

                if (place.address_components) {
                    this.parseAddressComponents(place);
                    this.showParsedAddress = true;
                    this.updateAddressFromParsed();
                    input.blur();
                }
            });
        },

        parseAddressComponents(place) {

            this.parsedAddress = {
                street: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                country: ''
            };

            // Shared parser: country-aware city/street fallbacks (UK
            // postal_town, sublocality wards, …) — see google-place-parser.
            const p = alvvaParsePlace(place);
            this.parsedAddress.city = p.city;
            this.parsedAddress.state = p.state;
            this.parsedAddress.country = p.country;
            this.parsedAddress.zip = p.zip;
            this.parsedAddress.street = p.street;
            // address2 (apt/suite/floor) isn't in Google's "address" type
            // results — left blank for the user to fill in manually.
        },

        updateAddressFromParsed() {
            // Build formatted address string
            const parts = [];
            if (this.parsedAddress.street) parts.push(this.parsedAddress.street);
            if (this.parsedAddress.address2) parts.push(this.parsedAddress.address2);
            if (this.parsedAddress.city) parts.push(this.parsedAddress.city);
            if (this.parsedAddress.state) parts.push(this.parsedAddress.state);
            if (this.parsedAddress.zip) parts.push(this.parsedAddress.zip);
            if (this.parsedAddress.country) parts.push(this.parsedAddress.country);

            this.employment.address = parts.join(', ');
            this.updateValue();
        },

        resetAddressSearch() {
            this.parsedAddress = {
                street: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                country: ''
            };
            this.employment.address = '';
            this.showParsedAddress = false;

            this.$nextTick(() => {
                if (this.$refs.addressSearchInput) {
                    this.$refs.addressSearchInput.value = '';
                    this.$refs.addressSearchInput.focus();
                }
            });

            this.updateValue();
        },

        parseExistingValue() {
            // Don't overwrite a field the user is actively editing — the
            // modelValue watcher fires on our own keystrokes too, which clobbered
            // fast typing (chars dropped / value flashed).
            if (this.$el && this.$el.contains(document.activeElement)) return;
            const value = this.modelValue || '';
            if (!value) return;

            try {
                const json = JSON.parse(value);
                if (typeof json === 'object' && json !== null) {
                    this.employment = {
                        employer: json.employer || '',
                        job_title: json.job_title || '',
                        address: json.address || '',
                        start_date: json.start_date || '',
                        end_date: json.end_date === 'Present' ? '' : (json.end_date || ''),
                        currently_employed: json.currently_employed === true || json.end_date === 'Present'
                    };

                    // Parse existing address into components if present
                    if (this.employment.address) {
                        this.parseExistingAddress(this.employment.address);
                        this.showParsedAddress = true;
                    }
                }
            } catch (e) {
                // Not JSON, might be legacy format
                this.employment.employer = value;
            }
        },

        parseExistingAddress(addressStr) {
            // Best-effort parse for comma-separated addresses. Part count tells
            // us which shape we're looking at — updateAddressFromParsed() only
            // ever emits 5 parts (no address2) or 6 parts (address2 filled), so
            // we key off that instead of guessing positions, which used to
            // mis-map address2 into city for legacy 5-part values.
            const parts = addressStr.split(',').map(p => p.trim());

            if (parts.length === 6) {
                // street, address2, city, state, zip, country
                this.parsedAddress.street = parts[0] || '';
                this.parsedAddress.address2 = parts[1] || '';
                this.parsedAddress.city = parts[2] || '';
                this.parsedAddress.state = parts[3] || '';
                this.parsedAddress.zip = parts[4] || '';
                this.parsedAddress.country = parts[5] || '';
            } else if (parts.length === 5) {
                // Legacy format saved before address2 existed: street, city,
                // state, zip, country
                this.parsedAddress.street = parts[0] || '';
                this.parsedAddress.address2 = '';
                this.parsedAddress.city = parts[1] || '';
                this.parsedAddress.state = parts[2] || '';
                this.parsedAddress.zip = parts[3] || '';
                this.parsedAddress.country = parts[4] || '';
            } else if (parts.length === 4) {
                // street, city, state, country — Google resolves no postal
                // code for many non-US addresses, so this shape is produced
                // TODAY, not just by legacy data.
                this.parsedAddress.street = parts[0] || '';
                this.parsedAddress.address2 = '';
                this.parsedAddress.city = parts[1] || '';
                this.parsedAddress.state = parts[2] || '';
                this.parsedAddress.zip = '';
                this.parsedAddress.country = parts[3] || '';
            } else if (parts.length === 3) {
                // city, state, country — Google resolved only a locality
                // (no street precision).
                this.parsedAddress.street = '';
                this.parsedAddress.address2 = '';
                this.parsedAddress.city = parts[0] || '';
                this.parsedAddress.state = parts[1] || '';
                this.parsedAddress.zip = '';
                this.parsedAddress.country = parts[2] || '';
            } else {
                // Unrecognized shape — don't guess field boundaries (that's how
                // address2 used to land in city), just show it as street.
                this.parsedAddress.street = addressStr;
                this.parsedAddress.address2 = '';
                this.parsedAddress.city = '';
                this.parsedAddress.state = '';
                this.parsedAddress.zip = '';
                this.parsedAddress.country = '';
            }
        },

        handleCurrentlyEmployedToggle() {
            if (this.employment.currently_employed) {
                this.employment.end_date = '';
            }
            this.updateValue();
        },

        formatValue() {
            // Don't save if no employer name
            if (!this.employment.employer.trim()) {
                return '';
            }

            return JSON.stringify({
                employer: this.employment.employer,
                job_title: this.employment.job_title,
                address: this.employment.address,
                start_date: this.employment.start_date,
                end_date: this.employment.currently_employed ? 'Present' : this.employment.end_date,
                currently_employed: this.employment.currently_employed
            });
        },

        updateValue() {
            const formatted = this.formatValue();
            this.modelValue = formatted;

            if (this.$refs.hiddenInput) {
                this.$refs.hiddenInput.value = formatted;
                this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    }
}
